/**
 * @file Submission read-only / review UI components — backgroundDetailsReadonly.ts.
 */

import {
  BACKGROUND_VALUE_UNIT_FIELDS,
  getBackgroundDataReviewerTemplateFields,
  getBackgroundDataTypeField,
  getBackgroundStageDisplayFields,
  partitionBackgroundFieldsAroundRawMaterials,
  readBackgroundValueForFormField,
  type StageFieldsSource,
} from "./backgroundStageFieldOrder";
import {
  dataTypeFieldOptions,
  normalizeDataTypeValue,
} from "./backgroundDataType";
import type { Material, UnitOption } from "../../pages/Dashboard/Contributor/Stages/BackgroundData/components/BackgroundFieldRenderer";
import type { SpecificityOption } from "../../pages/Dashboard/Contributor/Stages/BackgroundData/hooks/useBackgroundData";
import type { AnyObj, ReferenceLabels } from "./readonlyFormDisplay";
import { formatUnitSymbol } from "../../utils/formatUnitSymbol";
import {
  BACKGROUND_GEO_SELECTION_FIELD,
  BACKGROUND_GEO_SPEC_FIELD,
  parseBackgroundGeoFromApi,
} from "../../utils/backgroundGeographicalSpecificity";

/** Exported const — BACKGROUND PAIRED GROUPS. */
export const BACKGROUND_PAIRED_GROUPS: Record<string, string> = Object.fromEntries(
  Object.entries(BACKGROUND_VALUE_UNIT_FIELDS).map(([main, units]) => [main, units[0]])
);

/** Exported function — build Background Readonly Values. */
export function buildBackgroundReadonlyValues(
  background: AnyObj | null | undefined,
  rawMats: AnyObj[],
  dataReviewers: AnyObj[] = [],
  dataTypeOptions: ReturnType<typeof dataTypeFieldOptions> = []
): Record<string, string> {
  if (!background) return {};

  const bg = background;
  const { geoSpec, geoSelection } = parseBackgroundGeoFromApi(bg);
  const values: Record<string, string> = {
    materialId: String(bg.materialId ?? ""),
    materialCode: String(bg.materialCode ?? ""),
    subMaterialId: String(bg.subMaterialId ?? ""),
    subMaterialUnit: String(
      bg.subMaterialUnit ?? bg.subMaterialUnitId ?? bg.sub_material_unit_id ?? ""
    ),
    finishedQuantity: String(bg.finishedQuantity ?? ""),
    materialPropertiesAvailable: String(bg.materialPropertiesAvailable ?? ""),
    density: String(bg.density ?? ""),
    density_unit: bg.densityUnitId ? String(bg.densityUnitId) : "",
    uValue: String(bg.uValue ?? ""),
    uValue_unit: bg.uValueUnitId ? String(bg.uValueUnitId) : "",
    compressiveStrength: String(bg.compressiveStrength ?? ""),
    compressiveStrength_unit: bg.compressiveStrengthUnit
      ? String(bg.compressiveStrengthUnit)
      : "",
    tensileStrength: String(bg.tensileStrength ?? ""),
    tensileStrength_unit: bg.tensileStrengthUnit
      ? String(bg.tensileStrengthUnit)
      : "",
    temperature: String(bg.temperature ?? ""),
    temperature_unit: bg.temperatureUnitId ? String(bg.temperatureUnitId) : "",
    diameter: String(bg.diameter ?? ""),
    diameterUnit: bg.diameterUnitId ? String(bg.diameterUnitId) : "",
    coverage: String(bg.coverage ?? ""),
    coverageUnit: bg.coverageUnitId ? String(bg.coverageUnitId) : "",
    thickness: String(bg.thickness ?? ""),
    thicknessUnit: bg.thicknessUnitId ? String(bg.thicknessUnitId) : "",
    recycledContent: String(bg.recycledContent ?? ""),
    materialImageUrl: String(bg.materialImageUrl ?? ""),
    materialImage: String(bg.materialImageUrl ?? ""),
    dataType:
      normalizeDataTypeValue(bg.dataType, dataTypeOptions) ||
      String(bg.dataType ?? ""),
    dataReview: String(bg.dataReview ?? ""),
    dataAcquisition: String(bg.dataAcquisition ?? ""),
    dataCompleteness: String(bg.dataCompleteness ?? ""),
    [BACKGROUND_GEO_SPEC_FIELD]: geoSpec,
    [BACKGROUND_GEO_SELECTION_FIELD]: geoSelection,
    a1: bg.a1 ? "true" : "false",
    a2: bg.a2 ? "true" : "false",
    a3: bg.a3 ? "true" : "false",
    a1_a3: bg.a1_a3 ? "true" : "false",
    timelineAvailable: String(bg.timelineAvailable ?? ""),
    collectionStartYear: String(bg.collectionStartYear ?? ""),
    collectionStartMonth: String(bg.collectionStartMonth ?? ""),
    collectionStartDay: String(bg.collectionStartDay ?? ""),
    collectionEndYear: String(bg.collectionEndYear ?? ""),
    collectionEndMonth: String(bg.collectionEndMonth ?? ""),
    collectionEndDay: String(bg.collectionEndDay ?? ""),
    technologyId: String(bg.technologyId ?? ""),
    manufacturerId: String(bg.manufacturerId ?? ""),
  };

  for (const key of Object.keys(bg)) {
    const raw = bg[key];
    if (raw == null || raw === "") continue;
    if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
      if (!values[key]) values[key] = String(raw);
    }
  }

  rawMats.forEach((rm, idx) => {
    const i = idx + 1;
    const key = `rawMaterial_${String(i).padStart(2, "0")}`;
    values[key] = String(rm.rawMaterialName ?? rm.raw_material_name ?? "");
    values[`${key}_qty`] = String(rm.qty ?? "");
    values[`rawMaterialUnit_${String(i).padStart(2, "0")}`] = String(rm.unitId ?? "");
  });

  dataReviewers.forEach((row, idx) => {
    const i = idx + 1;
    values[`dataReviewerName_${String(i).padStart(2, "0")}`] = String(row.name ?? "");
    values[`dataReviewerEmail_${String(i).padStart(2, "0")}`] = String(row.email ?? "");
  });

  return values;
}

/** Exported function — background Enabled Pairs From Values. */
export function backgroundEnabledPairsFromValues(
  values: Record<string, string>
): Record<string, boolean> {
  return {
    density: Boolean(values.density?.trim()),
    uValue: Boolean(values.uValue?.trim()),
    compressiveStrength: Boolean(values.compressiveStrength?.trim()),
    tensileStrength: Boolean(values.tensileStrength?.trim()),
    temperature: Boolean(values.temperature?.trim()),
    diameter: Boolean(values.diameter?.trim()),
    coverage: Boolean(values.coverage?.trim()),
    thickness: Boolean(values.thickness?.trim()),
  };
}

/** Exported function — background Data Completeness Label. */
export function backgroundDataCompletenessLabel(values: Record<string, string>): string {
  const a1 = values.a1 === "true";
  const a2 = values.a2 === "true";
  const a3 = values.a3 === "true";
  const a1_a3 = values.a1_a3 === "true";

  if (a1 && a2 && a3) return "A1, A2, AND A3 life cycle stage data reported";
  if (a1_a3 && !a1 && !a2 && !a3)
    return "Aggregated A1-A3 life cycle data reported";
  const count = [a1, a2, a3].filter(Boolean).length;
  if (count === 2) return "Any two life cycle stage data reported";
  if (count === 1) return "Any one life cycle stage data reported";
  return String(values.dataCompleteness ?? "");
}

/** Exported function — resolve Background Stage From Schema. */
export function resolveBackgroundStageFromSchema(formSchema: AnyObj[]) {
  const stage = formSchema.find((s) => s.code === "BACKGROUND_DATA") as
    | StageFieldsSource
    | undefined;
  const orderedAll = getBackgroundStageDisplayFields(stage ?? null);
  const { before, after } = partitionBackgroundFieldsAroundRawMaterials(orderedAll);
  const { nameField, emailField } = getBackgroundDataReviewerTemplateFields(
    stage ?? null
  );
  const dataTypeField = getBackgroundDataTypeField(stage ?? null);
  return {
    orderedAll,
    fieldsBeforeRaw: before,
    fieldsAfterRaw: after,
    dataReviewerNameField: nameField,
    dataReviewerEmailField: emailField,
    dataTypeField,
  };
}

/** Exported function — read Bg Display Value. */
export function readBgDisplayValue(
  background: AnyObj | null | undefined,
  fieldName: string
): string {
  const raw = readBackgroundValueForFormField(background, fieldName);
  if (raw === null || raw === undefined) return "";
  return String(raw);
}

/** Exported function — build Background Readonly Units. */
export function buildBackgroundReadonlyUnits(
  references: ReferenceLabels
): UnitOption[] {
  const units = references.units ?? {};
  return Object.entries(units).map(([id, label]) => ({
    id: Number(id) || 0,
    propertyId: 0,
    unitSymbol: formatUnitSymbol(label),
    displayName: label,
  }));
}

/** Exported function — build Background Readonly Materials. */
export function buildBackgroundReadonlyMaterials(
  background: AnyObj | null | undefined,
  references: ReferenceLabels
): Material[] {
  const materialsMap = references.materials ?? {};
  const list: Material[] = Object.entries(materialsMap).map(([id, name]) => ({
    id,
    name,
    code: "",
    subMaterials: [],
  }));

  const materialId = String(background?.materialId ?? "").trim();
  if (materialId && !list.some((m) => m.id === materialId)) {
    const nested = background?.material as AnyObj | undefined;
    list.unshift({
      id: materialId,
      name: String(nested?.name ?? materialsMap[materialId] ?? materialId),
      code: String(background?.materialCode ?? nested?.code ?? ""),
      subMaterials: [],
    });
  }

  return list;
}

/** Exported function — build Background Readonly Sub Materials. */
export function buildBackgroundReadonlySubMaterials(
  references: ReferenceLabels
): { id: string; name: string }[] {
  return Object.entries(references.subMaterials ?? {}).map(([id, name]) => ({
    id,
    name,
  }));
}

/** Exported function — build Background Readonly Raw Materials. */
export function buildBackgroundReadonlyRawMaterials(
  rawMats: AnyObj[],
  references: ReferenceLabels
): { id: string; name: string }[] {
  const seen = new Set<string>();
  const out: { id: string; name: string }[] = [];

  const push = (name: string, id?: string) => {
    const n = name.trim();
    if (!n || seen.has(n)) return;
    seen.add(n);
    out.push({ id: id ?? n, name: n });
  };

  rawMats.forEach((rm) => {
    push(
      String(rm.rawMaterialName ?? rm.raw_material_name ?? ""),
      rm.rawMaterialId != null ? String(rm.rawMaterialId) : undefined
    );
  });

  Object.entries(references.rawMaterials ?? {}).forEach(([id, name]) => {
    push(name, id);
  });

  return out;
}

function specificityOption(
  id: unknown,
  name: unknown
): SpecificityOption | null {
  const sid = String(id ?? "").trim();
  if (!sid) return null;
  return { id: sid, name: String(name ?? sid) };
}

/** Exported function — build Background Readonly Technology Options. */
export function buildBackgroundReadonlyTechnologyOptions(
  background: AnyObj | null | undefined,
  references: ReferenceLabels
): SpecificityOption[] {
  const id = background?.technologyId;
  const fromRef = id != null ? references.technologies?.[String(id)] : undefined;
  const nested = background?.technology as AnyObj | undefined;
  const opt = specificityOption(
    id,
    nested?.name ?? background?.technologyValue ?? fromRef
  );
  return opt ? [opt] : [];
}

/** Exported function — build Background Readonly Manufacturer Options. */
export function buildBackgroundReadonlyManufacturerOptions(
  background: AnyObj | null | undefined,
  references: ReferenceLabels
): SpecificityOption[] {
  const id = background?.manufacturerId;
  const fromRef = id != null ? references.manufacturers?.[String(id)] : undefined;
  const nested = background?.manufacturer as AnyObj | undefined;
  const opt = specificityOption(
    id,
    nested?.name ?? background?.manufacturerValue ?? fromRef
  );
  return opt ? [opt] : [];
}
